import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Send the view back to the top when the route changes.
 *
 * Browsers do this for a normal page load; a client-side router does not, so
 * arriving at Settings from the bottom of a long Archive list would otherwise
 * drop you halfway down a page you have not seen the top of.
 *
 * The two shells scroll different things. V1/V2 scroll the window. V3 cannot —
 * #v3-shell is fixed to the viewport and #v3-content is the element with
 * overflow-y, so window.scrollTo() there does nothing at all. Both are reset
 * rather than branching on the theme: whichever is not in use is already at
 * zero, so resetting it costs nothing and there is no theme check to keep in
 * step with the shells.
 *
 * Keyed on pathname alone. Filters and paging live in the query string, and
 * yanking the page to the top every time someone adjusts a filter would fight
 * them while they work.
 *
 * useLayoutEffect, not useEffect: this runs after the new route's DOM is in
 * place but before paint, so there is no visible jump from the old scroll
 * position.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    // A link to an anchor is asking for a specific place on the page, not the
    // top of it. Nothing uses one today; this keeps that true if anything does.
    if (hash) return;

    document.getElementById('v3-content')?.scrollTo({ top: 0, left: 0 });
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname, hash]);

  return null;
}
